/** Volunteer form helpers: DTO builders, hydration, validation. */
import type {
  CreateVolunteerDto,
  CreateVolunteerFormState,
  EditVolunteerFormState,
  UpdateVolunteerDto,
} from '@/types/volunteer.types';
import {
  normalizeProfileGender,
  normalizeStaffAccountStatus,
  type ContactKind,
  type UserAccountStatus,
  type WeeklyAvailabilitySlot,
} from '@/types/user.types';

import {
  parseStoredPhone,
  phoneToE164,
  resolveStoredContact,
  nextContactFields,
  primaryContactKindFromType,
  assignPrimaryContactFields,
} from '@/utils/user-contact-utils';
import { toCalendarDateString, toLocalDateString } from '@/utils/format-date-utils';
import { getVolunteerFormDefaultRoleLabel } from './volunteer-configs';
import { getErrorMessage } from '@/utils/errors-utils';
import { findRoleByCode, getRoleLabel, type RoleCodeMatch } from '@/utils/role-label-utils';

/** Contact kind alias used by volunteer forms. */
type VolunteerContactType = ContactKind;
export type { VolunteerContactType };

/** Volunteer slice used when hydrating the edit form. */
type VolunteerRecord = {
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  email?: string | null;
  phone?: string | null;
  primaryContactKind?: 'email' | 'phone' | null;
  status?: string;
  managedById?: string | null;
  userRoles?: Array<{ roleId: string; role?: { label?: string } }>;
  userEstablishments?: Array<{
    establishmentId?: string;
    establishment?: { name?: string };
  }>;
  userProfiles?: Array<{
    engagementStartDate?: string | Date | null;
    engagementEndDate?: string | Date | null;
    subjects?: unknown[] | null;
    availability?: unknown;
    vscReportsFrequencies?: { daily: boolean; weekly: boolean } | null;
    gender?: string | null;
    targetHours?: number | null;
    exitReason?: string | null;
    exitReasonNote?: string | null;
    exitDate?: string | Date | null;
  }>;
};

/** Empty create form with the default phone country. */
export function emptyCreateVolunteerForm(defaultCountry: string): CreateVolunteerFormState {
  return {
    contact: '',
    contactType: 'unknown',
    contactCountryCode: defaultCountry,
    secondaryContact: '',
    secondaryContactCountryCode: defaultCountry,
    firstName: '',
    lastName: '',
    displayName: '',
    gender: '',
    roleIds: [],
    establishmentIds: [],
    managedById: '',
    subjects: [],
    availability: { weekly: [] },
    vscReportsFrequencies: { daily: false, weekly: false },
    engagementStartDate: new Date().toISOString().split('T')[0],
    engagementEndDate: '',
    status: 'active',
    targetHours: '',
  };
}

/** Empty edit form (hydrated later). */
export function emptyEditVolunteerForm(): EditVolunteerFormState {
  return {
    firstName: '',
    lastName: '',
    displayName: '',
    email: '',
    phone: '',
    primaryContactKind: undefined,
    status: 'active',
    roleIds: [],
    managedById: '',
    establishmentIds: [],
    subjects: [],
    availability: { weekly: [] },
    vscReportsFrequencies: { daily: false, weekly: false },
    engagementStartDate: '',
    engagementEndDate: '',
    gender: '',
    targetHours: '',
    exitReason: '',
    exitReasonNote: '',
    exitDate: '',
  };
}

/** Patch one create-form field. */
export function applyCreateVolunteerField(
  form: CreateVolunteerFormState,
  field: keyof CreateVolunteerFormState,
  value: unknown,
): CreateVolunteerFormState {
  if (field === 'managedById' && value !== form.managedById) {
    return { ...form, managedById: value as string, establishmentIds: [] };
  }
  return { ...form, [field]: value };
}

/** Clear or seed exit fields when status changes. */
function applyVolunteerStatusChange(
  form: EditVolunteerFormState,
  status: string,
): EditVolunteerFormState {
  if (status !== 'deactivated') {
    return { ...form, status, exitReason: '', exitReasonNote: '', exitDate: '' };
  }
  if (!form.exitDate) {
    return { ...form, status, exitDate: toLocalDateString(new Date()) };
  }
  return { ...form, status };
}

/** Patch one edit-form field. */
export function applyEditVolunteerField(
  form: EditVolunteerFormState,
  field: string,
  value: unknown,
): EditVolunteerFormState {
  const base: EditVolunteerFormState =
    field === 'managedById' && value !== form.managedById
      ? { ...form, managedById: value as string, establishmentIds: [] }
      : { ...form, [field]: value };
  if (field !== 'status') return base;
  return applyVolunteerStatusChange(base, value as string);
}

/** Next email/phone fields after a contact-type change. */
export function nextVolunteerContactState(
  prev: CreateVolunteerFormState,
  value: string,
  type: VolunteerContactType,
  countryCode: string | undefined,
  defaultCountry: string,
): CreateVolunteerFormState {
  const next = nextContactFields({
    previousType: prev.contactType,
    value,
    type,
    countryCode,
    previousCountryCode: prev.contactCountryCode,
    defaultCountry,
    previousSecondary: prev.secondaryContact,
    previousSecondaryCountry: prev.secondaryContactCountryCode,
  });
  return {
    ...prev,
    ...next,
    contactType: next.contactType as VolunteerContactType,
    contactCountryCode: next.contactCountryCode ?? prev.contactCountryCode,
  };
}

/** Hide coordinator picker for coordinator actors. */
export function shouldHideCoordinatorField(authRoles: Array<{ code: string }>): boolean {
  return (
    authRoles.some((role) => role.code.toLowerCase() === 'coordinator') &&
    !authRoles.some((role) => ['admin', 'manager'].includes(role.code.toLowerCase()))
  );
}

/** Effective managed-by user id (self for coordinators). */
export function resolveEffectiveManagedById(
  hideCoordinator: boolean,
  currentUserId: string | undefined,
  formManagedById: string,
): string {
  if (hideCoordinator && currentUserId) return currentUserId;
  return formManagedById;
}

/** Role ids to send on volunteer create. */
export function resolveCreateVolunteerRoleIds(params: {
  initialRole?: string;
  roles: RoleCodeMatch[];
  availableRoles: Array<{ value: string }>;
  isSingleRoleMode: boolean;
  currentRoleIdsLength: number;
}): string[] | undefined {
  let next: string[] | undefined;
  if (params.initialRole && params.roles.length > 0) {
    const matchingRole =
      findRoleByCode(params.roles, params.initialRole) ??
      (params.initialRole.toLowerCase() === 'vsc'
        ? findRoleByCode(params.roles, 'volunteer')
        : undefined) ??
      (params.initialRole.toLowerCase() === 'volunteer'
        ? findRoleByCode(params.roles, 'vsc')
        : undefined);
    if (matchingRole) next = [matchingRole.id];
  }
  if (
    params.isSingleRoleMode &&
    params.availableRoles.length === 1 &&
    params.currentRoleIdsLength === 0
  ) {
    const matchingRole = findRoleByCode(params.roles, params.availableRoles[0].value);
    if (matchingRole) next = [matchingRole.id];
  }
  return next;
}

/** Error when engagement end is before start. */
export function getEngagementEndOrderError(start: string, end: string): string | undefined {
  if (start && end && end <= start) return getErrorMessage('engagementEndAfterStart');
  return undefined;
}

/** Whether changing start should re-validate engagement end. */
export function shouldTouchEngagementEndOnStartChange(
  field: string,
  startValue: unknown,
  endDate: string,
): boolean {
  if (field !== 'engagementStartDate' || !endDate || typeof startValue !== 'string') {
    return false;
  }
  return !!startValue && endDate <= startValue;
}

/** Subject select options. */
export function mapVolunteerSubjectOptions(
  subjects: Array<{ id: string; label: string }>,
): Array<{ value: string; label: string }> {
  return subjects.map((subject) => ({ value: subject.id, label: subject.label }));
}

/** Establishment labels for a volunteer row. */
export function extractEstablishmentLabels(
  ueList: Array<{ establishmentId?: string; establishment?: { name?: string } }>,
): Array<{ id: string; name: string }> {
  const labelEntries: Array<{ id: string; name: string }> = [];
  for (const ue of ueList) {
    const id = ue.establishmentId;
    const name = ue.establishment?.name?.trim();
    if (id && name) labelEntries.push({ id, name });
  }
  return labelEntries;
}

/** Map a free-text Autre: reason to Autre. */
function normalizeExitReason(raw: string): string {
  return raw.startsWith('Autre:') ? 'Autre' : raw;
}

/** Extract the note from an Autre: exit reason. */
function normalizeExitReasonNote(note: string | null | undefined, rawReason: string): string {
  if (note) return note;
  if (rawReason.startsWith('Autre:')) return rawReason.slice('Autre:'.length).trim();
  return '';
}

/** Calendar date for exit, defaulting today when deactivated. */
function resolveExitDate(raw: string | Date | null | undefined, status: string): string {
  const parsed = toCalendarDateString(raw);
  if (parsed) return parsed;
  if (normalizeStaffAccountStatus(status) === 'deactivated') {
    return toLocalDateString(new Date());
  }
  return '';
}

/** ISO date (YYYY-MM-DD) for a date input. */
function toIsoDateInput(value: string | Date | null | undefined): string {
  if (!value) return '';
  return new Date(value).toISOString().split('T')[0];
}

/** Hydrate the edit form from a volunteer record. */
export function volunteerToEditFormData(
  volunteer: VolunteerRecord,
  params: {
    hideCoordinatorField: boolean;
    authUserId?: string;
    defaultCountry: string;
  },
): {
  formData: EditVolunteerFormState;
  phoneCountryCode: string;
  labelEntries: Array<{ id: string; name: string }>;
} {
  const ueList = volunteer.userEstablishments ?? [];
  const labelEntries = extractEstablishmentLabels(ueList);
  const managedByFromApi = volunteer.managedById ?? '';
  const initialManagedById = resolveEffectiveManagedById(
    params.hideCoordinatorField,
    params.authUserId,
    managedByFromApi,
  );
  const establishmentIds = ueList
    .map((ue) => ue.establishmentId)
    .filter((id): id is string => !!id);
  const stored = resolveStoredContact(
    volunteer.email ?? null,
    volunteer.phone ?? null,
    volunteer.primaryContactKind,
  );
  const secondaryPhone =
    stored.mode === 'email' && volunteer.phone
      ? parseStoredPhone(volunteer.phone, params.defaultCountry)
      : null;
  const phoneCountryCode =
    stored.mode === 'phone' ? stored.isoCode : (secondaryPhone?.isoCode ?? params.defaultCountry);
  const profile = volunteer.userProfiles?.[0];
  const rawExitReason = profile?.exitReason ?? '';
  const hours = profile?.targetHours;
  const status = normalizeStaffAccountStatus(String(volunteer.status));

  return {
    phoneCountryCode,
    labelEntries,
    formData: {
      firstName: volunteer.firstName || '',
      lastName: volunteer.lastName || '',
      displayName: volunteer.displayName || '',
      primaryContactKind: stored.mode,
      email: '',
      phone: stored.mode === 'phone' ? stored.displayValue : (secondaryPhone?.displayValue ?? ''),
      status,
      roleIds: (volunteer.userRoles?.map((ur) => ur.roleId) || []) as string[],
      managedById: initialManagedById,
      establishmentIds,
      subjects: (profile?.subjects || []) as string[],
      availability: (profile?.availability as { weekly: WeeklyAvailabilitySlot[] }) || {
        weekly: [],
      },
      vscReportsFrequencies: profile?.vscReportsFrequencies ?? { daily: false, weekly: false },
      engagementStartDate: toIsoDateInput(profile?.engagementStartDate),
      engagementEndDate: toIsoDateInput(profile?.engagementEndDate),
      gender: normalizeProfileGender(profile?.gender),
      targetHours: hours != null ? Number(hours) : '',
      exitReason: normalizeExitReason(rawExitReason),
      exitReasonNote: normalizeExitReasonNote(profile?.exitReasonNote, rawExitReason),
      exitDate: resolveExitDate(profile?.exitDate, status),
    },
  };
}

/** E.164 phone from primary or secondary contact. */
function resolveCreateVolunteerPhone(form: CreateVolunteerFormState): string | undefined {
  if (form.contactType === 'phone') {
    return phoneToE164(form.contact, form.contactCountryCode);
  }
  if (form.secondaryContact?.trim()) {
    return phoneToE164(form.secondaryContact, form.secondaryContactCountryCode);
  }
  return undefined;
}

/** Build `POST /users/invite` DTO from the create form. */
export function buildCreateVolunteerDto(
  form: CreateVolunteerFormState,
  effectiveManagedById: string,
): CreateVolunteerDto {
  return {
    inviteBy: form.contactType === 'email' ? 'email' : 'phone',
    primaryContactKind: primaryContactKindFromType(form.contactType),
    email: form.contactType === 'email' ? form.contact : form.secondaryContact?.trim() || undefined,
    firstName: form.firstName,
    lastName: form.lastName,
    phone: resolveCreateVolunteerPhone(form),
    roleIds: form.roleIds,
    establishmentIds: form.establishmentIds,
    managedById: effectiveManagedById || null,
    subjects: form.subjects.length > 0 ? form.subjects : undefined,
    availability: form.availability.weekly.length > 0 ? form.availability : undefined,
    vscReportsFrequencies: form.vscReportsFrequencies,
    engagementStartDate: form.engagementStartDate || undefined,
    engagementEndDate: form.engagementEndDate || undefined,
    gender: form.gender || undefined,
    targetHours: form.targetHours !== '' ? Number(form.targetHours) : undefined,
  };
}

/** Inputs for the volunteer edit validation schema payload. */
type VolunteerEditValidationPayloadParams = {
  form: EditVolunteerFormState;
  primaryContact: string;
  primaryContactType: 'email' | 'phone';
  primaryContactCountryCode: string;
  phoneCountryCode: string;
  primaryContactExists: boolean | null | undefined;
  contactExists: boolean | null | undefined;
};

/** Flatten edit-form state plus split primary contact into the edit schema payload. */
export function volunteerEditValidationPayload(
  params: VolunteerEditValidationPayloadParams,
): Record<string, unknown> {
  return {
    ...params.form,
    contact: params.primaryContact,
    _contactType: params.primaryContactType,
    contactCountryCode: params.primaryContactCountryCode,
    _phoneCountryCode:
      params.primaryContactType === 'email'
        ? params.phoneCountryCode
        : params.primaryContactCountryCode,
    _primaryContactExists: params.primaryContactExists,
    _contactExists: params.contactExists,
  };
}

/** Whether create submit is blocked by validation. */
export function isCreateVolunteerSubmitBlocked(
  isValid: boolean,
  form: CreateVolunteerFormState,
): boolean {
  return !isValid || !!getEngagementEndOrderError(form.engagementStartDate, form.engagementEndDate);
}

/** Write the typed primary contact onto email or phone and set `primaryContactKind`. */
export function applyVolunteerPrimaryContactChange(
  form: EditVolunteerFormState,
  value: string,
  type: 'email' | 'phone',
): EditVolunteerFormState {
  return {
    ...form,
    email: type === 'email' ? value : form.email,
    phone: type === 'phone' ? value : form.phone,
    primaryContactKind: type,
  };
}

/** Build `PATCH /users/:id` DTO from the edit form. */
export function buildUpdateVolunteerDto(
  form: EditVolunteerFormState,
  effectiveManagedById: string,
  contact: {
    primaryContact: string;
    primaryContactType: 'email' | 'phone';
    primaryContactCountryCode: string;
    secondaryContact?: string;
    secondaryContactCountryCode?: string;
    defaultCountry: string;
  },
): UpdateVolunteerDto {
  const dto: UpdateVolunteerDto = {
    firstName: form.firstName,
    lastName: form.lastName,
    displayName: form.displayName || undefined,
    status: form.status as UserAccountStatus,
    roleIds: form.roleIds.length > 0 ? form.roleIds : undefined,
    managedById: effectiveManagedById || null,
    establishmentIds: form.establishmentIds,
    subjects: form.subjects.length > 0 ? form.subjects : undefined,
    availability: form.availability.weekly.length > 0 ? form.availability : undefined,
    vscReportsFrequencies: form.vscReportsFrequencies,
    engagementStartDate: form.engagementStartDate || undefined,
    engagementEndDate: form.engagementEndDate || undefined,
    gender: form.gender || undefined,
    targetHours: form.targetHours !== '' ? Number(form.targetHours) : undefined,
    exitReason: form.exitReason || undefined,
    exitReasonNote: form.exitReasonNote || undefined,
    exitDate: form.status === 'deactivated' ? form.exitDate || undefined : undefined,
  };
  assignPrimaryContactFields({
    dto,
    primaryValue: contact.primaryContact,
    primaryKind: contact.primaryContactType,
    primaryCountry: contact.primaryContactCountryCode,
    secondaryValue: contact.secondaryContact,
    secondaryCountry: contact.secondaryContactCountryCode,
    defaultCountry: contact.defaultCountry,
  });
  return dto;
}

/** Whether edit submit is blocked by validation. */
export function isEditVolunteerSubmitBlocked(
  isValid: boolean,
  form: EditVolunteerFormState,
): boolean {
  return !isValid || !!getEngagementEndOrderError(form.engagementStartDate, form.engagementEndDate);
}

/** Role label for toasts / titles. */
export function resolveVolunteerRoleLabel(
  roles: Array<{ id: string; label?: string }>,
  roleId: string | undefined,
  fallback = getVolunteerFormDefaultRoleLabel(),
): string {
  return roles.find((role) => role.id === roleId)?.label || fallback;
}

/** Success toast after create/edit. */
export function formatVolunteerMutationSuccess(
  roleLabel: string | undefined,
  mode: 'create' | 'edit',
): string {
  const label = roleLabel || getVolunteerFormDefaultRoleLabel();
  if (mode === 'create') return `${label} créé avec succès !`;
  return `${label} modifié avec succès !`;
}

/** Helper text under the roles picker. */
export function getCreateVolunteerRolesHelperText(
  initialRole: string | undefined,
  hasRoleIds: boolean,
): string {
  if (initialRole && hasRoleIds) return 'Le rôle est pré-sélectionné';
  return `Sélectionnez le rôle à créer (${getRoleLabel('volunteer')}, ${getRoleLabel('professor').toLowerCase()}, etc.)`;
}

/** Format target hours for an input. */
export function formatTargetHoursInput(targetHours: '' | number): string {
  if (targetHours === '') return '';
  return String(targetHours ?? '');
}

/** Parse target hours from an input. */
export function parseTargetHoursInput(raw: string): '' | number {
  if (raw === '') return '';
  return Number(raw);
}

/** Helper text under the establishment picker. */
export function getEstablishmentHelperText(hasCoordinator: boolean, searchHint: string): string {
  if (hasCoordinator) return searchHint;
  return getErrorMessage('firstSelectCoordinator');
}
